// src/components/Auth/AuthWrapper.js - Key sections to fix auto-authentication
import React, { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { WalletContext } from '../../contexts/WalletContext';
import PasswordModal from './PasswordModal';
import DecryptionErrorModal from './DecryptionErrorModal';
import './AuthWrapper.css';

// Create a Context for the Authentication UI state that can be consumed by TabBar
export const AuthUIContext = React.createContext({
  isAuthenticating: false
});

/**
 * Authentication wrapper component that handles authentication flow
 * Shows login/create password screens when needed
 */
const AuthWrapper = ({ children }) => {
    const {
        isAuthenticated,
        hasPassword,
        isInitialized: authInitialized,
        logout,
        passwordDeferred,
        sessionActive,
        setSessionActive,
        sessionTimeout,
        autoAuthenticateWithDeferred,
        hasAutoPassword
      } = useContext(AuthContext);
  
  const {
    isInitialized: walletInitialized,
    userWallets,
    isEncrypted,
    decryptionError,
    clearDecryptionError,
    retryWalletDecryption,
    isDecrypting,
    hasStoredWallets,
    forceEncryptWallets
  } = useContext(WalletContext);
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordMode, setPasswordMode] = useState('login');
  const [timeoutWarning, setTimeoutWarning] = useState(false);
  const [showDecryptionErrorModal, setShowDecryptionErrorModal] = useState(false);
  const [securityCheckDone, setSecurityCheckDone] = useState(false);
  const [initialPromptDone, setInitialPromptDone] = useState(false);
  const [silentAuthInProgress, setSilentAuthInProgress] = useState(false);
  
  // State to determine if we're in authentication process (for hiding TabBar)
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use refs to track state changes without triggering re-renders
  const authCheckRef = useRef(false);
  // Expose authCheckRef to window for debugging and external access
  window.authCheckRef = authCheckRef;
  
  const lastAuthStateRef = useRef({
    isAuthenticated: false,
    hasPassword: false,
    isEncrypted: false,
    hasWallets: false
  });
  
// This detects if we're loading after a reset
useEffect(() => {
  // Check if we're loading after a fresh reset
  const isFreshReset = sessionStorage.getItem('bitdog_fresh_reset') === 'true';
  const hasResetParam = window.location.search.includes('reset=');
  
  if (isFreshReset || hasResetParam) {
    console.log("Loading after wallet reset - ensuring clean state");
    
    // Clear the reset flag
    sessionStorage.removeItem('bitdog_fresh_reset');
    
    // Force close any modals
    setShowPasswordModal(false);
    setShowDecryptionErrorModal(false);
    setIsAuthenticating(false);
    
    // Ensure we're in a clean URL to avoid reset parameter persisting
    if (hasResetParam && window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.origin);
    }
  }
  
  // If user previously skipped password, auto-authenticate silently without showing modal
  const silentlyAuthenticate = async () => {
    // Don't try again if we're already trying or are authenticated
    if (silentAuthInProgress || isAuthenticated) return;
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const expiredMobileSession = localStorage.getItem('bitdog_session_expired') === 'true';
    const needsAutoReauth = localStorage.getItem('bitdog_auto_reauth') === 'true';
    
    // IMPROVED: Always try silent auto-auth for users with passwordDeferred (no password set)
    // or expired mobile sessions with auto-password, or when auto-reauth is needed
    if (hasStoredWallets && 
       ((passwordDeferred || expiredMobileSession || needsAutoReauth) && hasAutoPassword) && 
       !isAuthenticated) {
      
      console.log("Attempting silent auto-authentication");
      setSilentAuthInProgress(true);
      
      try {
        // Attempt auto-auth without showing any modals
        const success = await autoAuthenticateWithDeferred();
        
        if (success) {
          console.log("Silent auto-authentication successful");
          // Clear any expired session flags
          localStorage.removeItem('bitdog_session_expired');
          localStorage.removeItem('bitdog_auto_reauth'); // Clear the auto-reauth flag
          
          // Auto-decrypt wallets - IMPORTANT: This is a more comprehensive wallet decryption
          // which now also decrypts the seed phrase properly
          await retryWalletDecryption();
          
          // Explicitly force navigation to wallet page after successful auto-auth
          if (window.location.pathname === '/') {
            console.log("Auto-redirecting to wallet page");
            setTimeout(() => {
              window.location.href = '/wallet';
            }, 100);
          }
        } else {
          console.log("Silent auto-authentication failed");
          
          // Only show the password modal if silent auth failed AND user has a password set
          if (hasPassword && !passwordDeferred) {
            console.log("Showing auth modal after failed silent authentication");
            setPasswordMode('login');
            setShowPasswordModal(true);
            setIsAuthenticating(true);
          }
        }
      } catch (err) {
        console.error("Error during silent auto-authentication:", err);
      } finally {
        setSilentAuthInProgress(false);
      }
    }
  };
  
  // Try to authenticate silently
  silentlyAuthenticate();
  
}, [hasStoredWallets, passwordDeferred, isAuthenticated, silentAuthInProgress, 
   autoAuthenticateWithDeferred, hasAutoPassword, hasPassword, retryWalletDecryption]);

  useEffect(() => {
    setIsAuthenticating(showPasswordModal || showDecryptionErrorModal);
  }, [showPasswordModal, showDecryptionErrorModal]);
  
// Initial prompt check - runs once when component mounts
useEffect(() => {
  if (!initialPromptDone && authInitialized && walletInitialized && !silentAuthInProgress) {
    // FIXED: Only execute once authentication check is complete
    console.log("Running initial prompt check...");
    
    // Don't show any modals while silent auth is in progress
    if (silentAuthInProgress) {
      console.log("Silent auth in progress, deferring prompt check");
      return;
    }
    
    // IMPROVED: For users with no password set (auto-security only), try silent auth
    // without showing any modals
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const expiredMobileSession = localStorage.getItem('bitdog_session_expired') === 'true';
    
    if (passwordDeferred && hasAutoPassword) {
      console.log("User uses auto-security only, attempting silent authentication");
      autoAuthenticateWithDeferred().then(success => {
        if (success) {
          console.log("Silent auto-authentication successful");
          // Clear any expired session flags
          localStorage.removeItem('bitdog_session_expired');
          
          // Try to decrypt the wallet
          retryWalletDecryption();
        } else {
          console.log("Silent auto-authentication failed, will show prompt only if has password");
          // Only show prompt if user has an actual password set
          if (hasPassword) {
            setPasswordMode('login');
            setShowPasswordModal(true);
            setIsAuthenticating(true);
          }
        }
      });
      setInitialPromptDone(true);
      return;
    }
    
    // For users with password set, only show login modal
    const shouldShowPasswordModal = hasStoredWallets && 
                                   isEncrypted && 
                                   userWallets.length === 0 && 
                                   !isDecrypting &&
                                   !passwordDeferred &&
                                   hasPassword; // Only if user has actually set a password
    
    if (shouldShowPasswordModal) {
      console.log("Wallet data detected with password set, showing password prompt");
      setPasswordMode('login');
      setShowPasswordModal(true);
      setIsAuthenticating(true); // Set authenticating state to hide TabBar
    }
    
    setInitialPromptDone(true);
  }
  
  // Set loading state
  if (authInitialized && walletInitialized) {
    setIsLoading(false);
  }
}, [
  authInitialized, 
  walletInitialized, 
  hasStoredWallets, 
  isEncrypted, 
  userWallets, 
  isDecrypting, 
  initialPromptDone, 
  passwordDeferred, 
  autoAuthenticateWithDeferred,
  silentAuthInProgress,
  hasAutoPassword,
  hasPassword,
  retryWalletDecryption
]);

  // One-time security check that runs on component mount - modified version
  useEffect(() => {
    if (!securityCheckDone && authInitialized && walletInitialized && !silentAuthInProgress) {
      console.log("Running initial security check...");
      
      // Store initial state in ref
      lastAuthStateRef.current = {
        isAuthenticated,
        hasPassword,
        isEncrypted,
        hasWallets: userWallets?.length > 0 || hasStoredWallets
      };
      
      // IMPORTANT: Check if we're in a post-reset state
      // If no wallets in storage and none in memory, but we still have password
      // This could be a post-reset scenario where state wasn't fully cleared
      const possiblePostReset = !hasStoredWallets && userWallets?.length === 0 && hasPassword;
      
      if (possiblePostReset) {
        console.log("Detected possible post-reset state, ensuring clean state");
        setShowPasswordModal(false);
        setIsAuthenticating(false);
        
        // Force a refresh one more time to ensure we get to the welcome page
        if (window.location.search.indexOf('fresh=') === -1) {
          window.location.replace(window.location.origin + "?fresh=" + Date.now());
          return;
        }
      }
      
      // IMPROVED: Always try silent auth for password-deferred (auto-security) users first
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const expiredMobileSession = localStorage.getItem('bitdog_session_expired') === 'true';
      
      if (hasStoredWallets && passwordDeferred && hasAutoPassword) {
        console.log("Security check: User uses auto-security, attempting silent authentication");
        autoAuthenticateWithDeferred().then(success => {
          if (!success) {
            console.log("Silent authentication failed, but user uses auto-security - no password modal needed");
            // Don't show password modal for auto-security users
          }
        });
      }
      // Only show password modal if user has explicitly set a password
      else if (hasStoredWallets && isEncrypted && !isAuthenticated && hasPassword) {
        console.log("Security check: Requiring authentication for encrypted wallet with password");
        setPasswordMode('login');
        setShowPasswordModal(true);
        setIsAuthenticating(true); // Set authenticating state
        authCheckRef.current = true;
      } 
      // Legacy unencrypted wallets that need encryption
      else if (userWallets?.length > 0 && !isEncrypted && hasPassword) {
        console.log("Security check: Unencrypted wallets with password set - showing login to encrypt");
        setPasswordMode('login');
        setShowPasswordModal(true);
        setIsAuthenticating(true); // Set authenticating state
        authCheckRef.current = true;
      }
      // Has wallets but no password and not deferred - need to create password
      else if (userWallets?.length > 0 && !hasPassword && !passwordDeferred) {
        console.log("Security check: Showing create password modal");
        setPasswordMode('create');
        setShowPasswordModal(true);
        setIsAuthenticating(true); // Set authenticating state
        authCheckRef.current = true;
      }
      
      setSecurityCheckDone(true);
    }
  }, [
    authInitialized, 
    walletInitialized, 
    isAuthenticated, 
    hasPassword, 
    isEncrypted, 
    hasStoredWallets, 
    userWallets, 
    passwordDeferred,
    securityCheckDone,
    autoAuthenticateWithDeferred,
    silentAuthInProgress,
    hasAutoPassword
  ]);
  
  // Monitor wallet state to detect when wallet data is missing
  useEffect(() => {
    // If we have encrypted wallets, but no wallets are loaded, and we have an active session
    // This means auto-decryption failed but we should still be authenticated
    if (authInitialized && 
        walletInitialized && 
        hasStoredWallets && 
        isEncrypted && 
        userWallets.length === 0 && 
        sessionActive && 
        !showPasswordModal && 
        !isDecrypting && 
        !silentAuthInProgress) {
      
      // Try auto-authentication if password is deferred
      if (passwordDeferred && hasAutoPassword) {
        console.log("Password deferred, attempting auto-authentication");
        
        autoAuthenticateWithDeferred().then(success => {
          if (!success) {
            // For auto-security users, don't show password modal
            console.log("Auto-authentication failed, but user has auto-security only - no password prompt needed");
          }
        });
      } 
      // Only show password modal if user actually set a password
      else if (hasPassword) {
        console.log("Detected missing wallet data with active session - showing password prompt");
        setPasswordMode('login');
        setShowPasswordModal(true);
        setIsAuthenticating(true); // Set authenticating state
      }
    }
  }, [
    authInitialized,
    walletInitialized,
    hasStoredWallets,
    isEncrypted,
    userWallets.length,
    sessionActive,
    showPasswordModal,
    isDecrypting,
    passwordDeferred,
    autoAuthenticateWithDeferred,
    silentAuthInProgress,
    hasAutoPassword,
    hasPassword
  ]);
  
// Handle password modal success
const handlePasswordSuccess = () => {
  console.log("Password operation successful");
  authCheckRef.current = false;
  setShowPasswordModal(false);
  setIsAuthenticating(false); // Clear authenticating state
  
  // Ensure session is marked as active
  if (!sessionActive) {
    setSessionActive(true);
  }
  
  // Clear any expired session markers (for mobile devices)
  localStorage.removeItem('bitdog_session_expired');
  
  // Force a small delay before attempting wallet decryption again
  // This helps ensure all state updates have propagated
  setTimeout(() => {
    retryWalletDecryption();
  }, 100);
};
  
  // Handle password modal cancel - modified to handle reset cases
  const handlePasswordCancel = (e) => {
    if (e) e.preventDefault();
    
    authCheckRef.current = false;
    
    if (passwordMode === 'login') {
      // Just close without logging in - will require login again when needed
      console.log("Login canceled");
      setShowPasswordModal(false);
      setIsAuthenticating(false); // Clear authenticating state
    } else if (passwordMode === 'create' && (passwordDeferred || userWallets.length === 0)) {
      // Allow skipping if password is deferred OR there are no wallets (reset case)
      console.log("Password creation deferred or no wallets present");
      setShowPasswordModal(false);
      setIsAuthenticating(false); // Clear authenticating state
      
      // If we have no wallets, force a page reload to ensure clean state
      if (userWallets.length === 0) {
        setTimeout(() => {
          window.location.replace(window.location.origin);
        }, 100);
      }
    }
  };
  
  // Handle decryption error modal retry
  const handleDecryptionErrorRetry = () => {
    // Show the password modal again to retry with a new password
    setShowDecryptionErrorModal(false);
    clearDecryptionError();
    setPasswordMode('login');
    setShowPasswordModal(true);
    // Keep isAuthenticating true since we're still in the auth flow
  };
  
  // Handle decryption error modal close
  const handleDecryptionErrorClose = () => {
    setShowDecryptionErrorModal(false);
    clearDecryptionError();
    setIsAuthenticating(false); // Clear authenticating state
  };
  
  // Show timeout warning when session is about to expire
  useEffect(() => {
    if (!isAuthenticated) {
      setTimeoutWarning(false);
      return;
    }
    
    // Calculate warning time - 1 minute before session expires, or 10% of the session time for short sessions
    const warningTime = Math.min(sessionTimeout - 60000, sessionTimeout * 0.9);
    
    // Show warning before session expires
    const warningTimeout = setTimeout(() => {
      setTimeoutWarning(true);
    }, warningTime);
    
    return () => clearTimeout(warningTimeout);
  }, [isAuthenticated, sessionTimeout]);
  
  // Hide timeout warning when user interacts
  useEffect(() => {
    if (!timeoutWarning) return;
    
    const hideWarning = () => {
      setTimeoutWarning(false);
    };
    
    window.addEventListener('click', hideWarning);
    window.addEventListener('keydown', hideWarning);
    
    return () => {
      window.removeEventListener('click', hideWarning);
      window.removeEventListener('keydown', hideWarning);
    };
  }, [timeoutWarning]);
  
  
  return (
    <AuthUIContext.Provider value={{ isAuthenticating }}>
      <div className="auth-wrapper">
        {/* Password modal */}
        <PasswordModal
          isOpen={showPasswordModal}
          mode={passwordMode}
          onSuccess={handlePasswordSuccess}
          onCancel={handlePasswordCancel}
        />
        
        {/* Decryption Error Modal */}
        <DecryptionErrorModal
          isOpen={showDecryptionErrorModal}
          message={decryptionError}
          onRetry={handleDecryptionErrorRetry}
          onClose={handleDecryptionErrorClose}
          isLoading={isDecrypting}
        />
        
        {/* Main content */}
        {children}
      </div>
    </AuthUIContext.Provider>
  );
};

export default AuthWrapper;